/**
 * Entitlements front — miroir de backend/app/core/entitlements.py
 * (modules commerciaux × scopes rôle → bandes / sections du menu visibles).
 */
import { NAV } from '../components/Sidebar';

export const ALL_MODULES = ['rh', 'financials', 'simulation', 'scenarii'];

// Trois périmètres = trois classes de sensibilité. L'ex-périmètre `performance`
// (Rentabilité) a été retiré le 2026-08-11 : croisement de People et Finance, pas
// une classe de données. Miroir du backend.
export const ALL_SCOPES = ['people', 'financials', 'sales', 'governance'];

export const MODULE_SCOPES = {
  rh: ['people'],
  // Périmètre unique pour tout l'argent : compte de résultat, bilan ET
  // trésorerie. L'ex-périmètre `cash` a été fusionné dans `financials` le
  // 2026-08-10 (miroir du backend).
  // Le Cockpit (dont le journal de gouvernance) fait partie du socle vendu
  // avec Finance.
  financials: ['financials', 'governance'],
  // Rentabilité : périmètre People seul (décision du 02.09.2026, miroir du
  // serveur). Tout ce qu'elle calcule vient de People ; Finance n'y lit rien.
  simulation: ['people'],
  // Module `scenarii` relabellisé « Ventes & Opérations » le 2026-08-12 (id
  // conservé = clé pack commercial). S'ouvre aussi à `financials` : la section
  // Achats & stock y vit désormais, et Finance doit pouvoir y planifier.
  scenarii: ['sales', 'financials'],
};

/** Sections : au moins un de ces scopes en lecture pour afficher l'entrée. */
export const PAGE_SCOPES = {
  'hr-story': ['people'],
  'hr-synthese': ['people'],
  'hr-headcount': ['people'],
  'hr-home': ['people'],
  'hr-payroll-detail': ['people'],
  'hr-social-hub': ['people'],
  'hr-lpp': ['people'],
  'hr-personnel': ['people'],
  'hr-expenses': ['people'],
  'hr-planning': ['people'],
  'hr-trajectory': ['people'],
  'hr-time-hub': ['people'],
  'time-standard': ['people'],
  // Section rangée au Chantier (bande `adminOnly`) : la garde qui s'applique
  // aujourd'hui est `isPlatformAdmin`. Ce périmètre décrit ce qu'elle exigera
  // le jour où elle rejoindra People.
  'hr-time-pilot': ['people'],
  'hr-lcr-hub': ['people'],
  'cost-rates-hub': ['people'],
  // Sections extraites de deux hubs le 2026-08-27 : les groupes ressources et
  // le catalogue de services. Le serveur les réserve à People (`/api/tdabc/rc`,
  // 02.09.2026) : l'écran s'y accorde (2026-10-12), sinon Finance les voyait au
  // menu et se heurtait à une erreur 403.
  'cost-pools': ['people'],
  'indirect-items': ['people'],
  'rc-services': ['people'],
  'rc-allocation': ['people'],
  'hr-people-hub': ['people'],
  /* LES ÉTATS EN ACCÈS DIRECT (2026-09-17) : une clé par section du menu. Le
     hub à onglets reste routé hors menu (relais du Cockpit), donc gardé. */
  ...Object.fromEntries(['financials-synthesis', 'financials-pl', 'financials-bs', 'financials-cf',
    'financials-cash-plan', 'financials-indicators']
    .map((k) => [k, ['financials']])),
  /* FOCUS+ est une SECTION de Finance depuis le 2026-09-18 : il lit les états
     financiers, il en suit donc le périmètre. Il figurait jusque-là parmi les
     pages toujours ouvertes, héritage du temps où seul le Cockpit y menait,
     dont l'onglet exigeait pourtant déjà `financials`. */
  'custom-reports': ['financials'],
  'financials-reports-hub': ['financials'],
  'financials-bfre': ['financials'],
  'forecast-hub': ['financials'],
  'forecast-wc': ['financials'],
  'forecast-out': ['financials'],
  // Business plan (2026-09-29) : la Planification sur l'horizon du client.
  'business-plan': ['financials'],
  'fin-wc': ['financials'],
  'forecast-synth': ['financials'],
  // Hub de la réorganisation Finance 2026-08-18 (les ex-hubs Exploitation et
  // Investissement & financement, dissous le 2026-08-19, sont devenus des
  // onglets de Trésorerie et du Prévisionnel).
  /* LE GROUPE OPÉRATIONS (Investissements, Charges directes, variables et
     fixes) S'OUVRE AU RÔLE OPÉRATIONS (2026-09-29), par `/api/sop` (cf.
     api/opsRoute.js) ; Finance & RH lui reste fermé. */
  'financials-capex-plan': ['financials', 'sales'],
  'fin-budget': ['financials', 'sales'],
  'special-ops': ['financials'],
  'fin-payroll': ['financials'],
  'financials-financing': ['financials'],
  'financials-assets': ['financials'],
  'tresorerie-hub': ['financials'],
  ...Object.fromEntries(['fc-pnl-hub', 'fc-bs-hub', 'fc-cf-hub', 'fc-invest', 'pos-clients', 'pos-fourn', 'pos-advances', 'fc-cash',
    'fc-financing', 'fc-balance', 'fc-cashflow']
    .map((k) => [k, ['financials']])),
  // Charges directes et Charges variables : même ouverture (voir plus haut).
  'fc-stock': ['financials', 'sales'],
  'fc-variable': ['financials', 'sales'],
  // Stock & achats produits : Finance a la section complète ; un profil `sales`
  // seul reçoit la vue acheteur restreinte servie par /api/sop (liste blanche
  // serveur : stock / achats / consommation).
  'sales-stock-purchases': ['financials', 'sales'],
  'direct-costs': ['financials', 'sales'],
  'financials-data-hub': ['financials'],
  'financials-input-hub': ['financials'],
  'financials-mapping-hub': ['financials'],
  'financials-settings-hub': ['financials'],
  'chr-pcr': ['people'],
  'tdabc-rentabilite-client': ['people'],
  'sales-billing': ['sales'],
  'sales-source-maps': ['sales'],
  'sales-forecast': ['sales'],
  'sales-atterrissage': ['sales'],
  'sales-revenue': ['sales'],
  'sales-offers-list': ['sales'],
  'sales-trajectory': ['sales'],
  'sales-monthly': ['sales'],
  'sales-backlog': ['sales'],
  'sales-portfolio': ['sales'],
  'sales-plan': ['sales'],
  'sales-service-plan': ['sales'],
  treasury: ['financials'],
};

/**
 * Libellés des périmètres — clés i18n (namespace nav). Résoudre via t() au rendu.
 * Alignés sur les noms du menu.
 */
export const SCOPE_LABEL_KEYS = {
  people: 'nav:scopes.people',
  financials: 'nav:scopes.financials',
  sales: 'nav:scopes.sales',
  governance: 'nav:scopes.governance',
};

/** @deprecated préférer SCOPE_LABEL_KEYS + t() — FR figé pour imports legacy */
export const SCOPE_LABELS = {
  people: 'People',
  financials: 'Finance',
  sales: 'Ventes',
  governance: 'Gouvernance',
};

/** Rôles système : slug renvoyé par l'API → clé i18n. */
export const ROLE_LABEL_KEYS = {
  admin: 'nav:roles.admin',
  ceo: 'nav:roles.ceo',
  'full-access': 'nav:roles.full-access',
  finance: 'nav:roles.finance',
  hr: 'nav:roles.hr',
  sales: 'nav:roles.sales',
  viewer: 'nav:roles.viewer',
};

/** @deprecated préférer ROLE_LABEL_KEYS + t() */
export const ROLE_LABELS = {
  admin: 'Administrateur',
  ceo: 'CEO',
  'full-access': 'Accès complet',
  finance: 'Finance',
  hr: 'RH',
  sales: 'Ventes',
  viewer: 'Lecteur',
};

export const MODULE_LABEL_KEYS = {
  rh: 'nav:modules.rh',
  financials: 'nav:modules.financials',
  simulation: 'nav:modules.simulation',
  scenarii: 'nav:modules.scenarii',
};

/** @deprecated préférer MODULE_LABEL_KEYS + t() */
export const MODULE_LABELS = {
  rh: 'People',
  financials: 'Finance',
  simulation: 'Rentabilité',
  scenarii: 'Ventes & Ops',
};

/** Pages toujours accessibles (hors modules commerciaux). */
const ALWAYS_ALLOWED = new Set([
  'welcome',
  'welcome-news',
  'welcome-guides',
  'welcome-fiches',
  'home',
  'admin-hub',
]);

/**
 * Rôles autorisés à ouvrir « Paramètres » (réglages du client : devise, année
 * d'analyse, TVA, lignes de crédit, mois du 13e/14e) — décision 2026-08-09.
 * Les réglages engagent tout le client, pas un périmètre : ils suivent une liste
 * de rôles, pas les scopes de données. Miroir serveur : `SETTINGS_ROLES` dans
 * backend/app/core/entitlements.py (qui refuse aussi l'écriture, pas seulement
 * l'entrée de menu).
 */
export const SETTINGS_ROLES = new Set(['admin', 'ceo', 'full-access', 'finance']);

/** true si la personne peut ouvrir les réglages du client actif. */
export function canManageSettings(roles, isPlatformAdmin) {
  if (isPlatformAdmin) return true;
  return (roles || []).some((r) => SETTINGS_ROLES.has(r));
}

/**
 * Paramètres › Données (2026-10-11) : l'export Excel et la sauvegarde JSON
 * sortent toutes les données du client, salaires compris. Accès aux
 * Paramètres, plus la lecture de People ET de Finance ; la règle suit les
 * périmètres, jamais le nom du rôle. Miroir serveur : `can_export_data`.
 */
export const DATA_EXPORT_SCOPES = ['people', 'financials'];

export function canExportData(roles, scopes, isPlatformAdmin) {
  if (isPlatformAdmin) return true;
  return canManageSettings(roles, false)
    && DATA_EXPORT_SCOPES.every((s) => canReadScope(scopes, s));
}

/**
 * Bandes RÉSERVÉES À L'EXPLOITANT (drapeau `adminOnly` dans NAV) — aujourd'hui
 * le Chantier. Elles ne sont PAS des modules commerciaux : rien à cocher dans
 * le pack d'un client, et pour cause — activer un « module chantier » chez un
 * client lui montrerait justement les écrans qu'on lui cache (décision Olivier
 * 2026-08-26, après une première version qui passait par le pack).
 */
function adminOnlyBand(pageKey) {
  return NAV.find((n) => n.type === 'band' && n.adminOnly && bandKeys(n).includes(pageKey)) || null;
}

function bandKeys(band) {
  if (band.items) return band.items.filter((i) => i.key).map((i) => i.key);
  if (band.groups) return band.groups.flatMap((g) => g.items.map((i) => i.key));
  return [];
}

function canReadScope(scopes, scopeId) {
  const level = (scopes || {})[scopeId];
  return level === 'r' || level === 'rw';
}

function pageAllowedByScope(pageKey, scopes) {
  const needed = PAGE_SCOPES[pageKey];
  if (!needed || !needed.length) return true;
  return needed.some((s) => canReadScope(scopes, s));
}

/**
 * Modules visibles = activés côté client ∩ lecture sur un scope du module.
 * Pack absent / vide = aucun module (pas de repli permissif — miroir du backend).
 */
export function computeVisibleModules(enabledModules, scopes) {
  const enabled = !enabledModules
    ? []
    : enabledModules.filter((m) => ALL_MODULES.includes(m));
  const sc = scopes || {};
  return ALL_MODULES.filter((mid) => {
    if (!enabled.includes(mid)) return false;
    return (MODULE_SCOPES[mid] || []).some((s) => canReadScope(sc, s));
  });
}

/**
 * Modules ACHETÉS par le client mais FERMÉS au rôle (demande d'Olivier,
 * 2026-10-12) : ils restent à leur place dans la ligne des modules, grisés et
 * cadenassés, plutôt que de disparaître. Un module que le client n'a pas
 * acheté ne s'y montre pas.
 */
export function computeLockedModules(enabledModules, scopes) {
  const visible = new Set(computeVisibleModules(enabledModules, scopes));
  return ALL_MODULES.filter((mid) => (enabledModules || []).includes(mid) && !visible.has(mid));
}

/**
 * Filtre NAV : modules visibles, puis sections selon scopes
 * (ex. Ventes voit Facturation mais pas Simulation liquidité).
 */
export function filterNav(visibleModules, scopes = {}, isPlatformAdmin = false) {
  const allow = new Set(visibleModules || []);
  return NAV.map((node) => {
    if (node.type === 'page') return node;
    // Chantier : hors pack, visible du seul exploitant.
    if (node.adminOnly) return isPlatformAdmin ? node : null;
    if (node.type !== 'band' || !allow.has(node.id)) return null;

    // Modules à ÉTAPES GROUPÉES (Finance, Planification) : leurs sections vivent
    // dans `groups`, pas dans `items`. Elles échappaient au filtre par périmètre
    // — sans conséquence tant que toutes relevaient du même, mais toute section
    // d'un autre périmètre s'y serait affichée pour tout le monde.
    if (node.groups) {
      // `needsModule` : une section qui demande un autre module du pack
      // (Résultat prévisionnel et Business plan, Finance). Elle reste au menu,
      // GRISÉE (`off`), pour que le groupe garde la même structure (2026-09-30) ;
      // `isPageAllowed` refuse toujours d'ouvrir la page.
      const groups = node.groups
        .map((g) => ({
          ...g,
          items: (g.items || []).filter((item) => pageAllowedByScope(item.key, scopes))
            .map((item) => (item.needsModule && !allow.has(item.needsModule) ? { ...item, off: true } : item)),
        }))
        .filter((g) => g.items.length);
      if (!groups.length) return null;
      return { ...node, groups };
    }

    if (!node.items) return node;
    const items = node.items.filter((item) => pageAllowedByScope(item.key, scopes));
    if (!items.length) return null;
    return { ...node, items };
  }).filter(Boolean);
}

/** Module commercial portant une clé de page (bande NAV ou route imbriquée). */
export function moduleForPage(pageKey) {
  if (!pageKey || ALWAYS_ALLOWED.has(pageKey)) return null;
  for (const node of NAV) {
    // Une bande `adminOnly` (le Chantier) n'est pas un module commercial : elle
    // ne peut pas être « le module de cette page », sinon un appelant croirait
    // devoir la chercher dans le pack d'un client.
    if (node.adminOnly) continue;
    if (node.type === 'band' && bandKeys(node).includes(pageKey)) return node.id;
  }
  if (
    pageKey.startsWith('hr-') ||
    pageKey.startsWith('analytics')
  ) {
    return 'rh';
  }
  if (
    pageKey.startsWith('financials') ||
    pageKey.startsWith('profit') ||
    pageKey.startsWith('pl-') ||
    pageKey.startsWith('bs-') ||
    pageKey.startsWith('balance') ||
    pageKey.startsWith('cash-flow') ||
    pageKey.startsWith('forecast')
  ) {
    return 'financials';
  }
  if (pageKey.startsWith('tdabc') || pageKey.startsWith('chr-')) return 'simulation';
  if (pageKey.startsWith('sales')) return 'scenarii';
  // Ni le Chantier ni `treasury` ne revendiquent de module commercial : le
  // Chantier n'en est pas un, et `treasury` est aujourd'hui dans sa bande. Une
  // clé en dur ici aurait survécu au déménagement en rouvrant l'écran par
  // Finance.
  return null;
}

/** Tous les modules dont une bande porte la page : une section COMMUNE
    (groupe Planification, 2026-09-29) en a deux, et s'ouvre par l'un ou
    l'autre. Hors bande, le module de `moduleForPage`. */
export function modulesForPage(pageKey) {
  if (!pageKey || ALWAYS_ALLOWED.has(pageKey)) return [];
  const bands = NAV.filter((n) => !n.adminOnly && n.type === 'band' && bandKeys(n).includes(pageKey));
  if (bands.length) return bands.map((b) => b.id);
  const mid = moduleForPage(pageKey);
  return mid ? [mid] : [];
}

/** Le module exigé en plus par une section (`needsModule`), s'il y en a un. */
function neededModule(pageKey) {
  for (const n of NAV) {
    if (n.type !== 'band') continue;
    const it = (n.groups ? n.groups.flatMap((g) => g.items || []) : (n.items || [])).find((i) => i.key === pageKey);
    if (it && it.needsModule) return it.needsModule;
  }
  return null;
}

/** true si la page est autorisée (module visible + scope section). */
export function isPageAllowed(pageKey, visibleModules, { isPlatformAdmin, scopes, roles } = {}) {
  if (pageKey === 'admin-hub') return Boolean(isPlatformAdmin);
  // Journal d'audit : exploitant seulement, avant tout raisonnement par module
  // (sa bande NAV le rattacherait sinon à un module « audit » inexistant).
  if (pageKey === 'audit-log') return Boolean(isPlatformAdmin);
  if (pageKey === 'settings-hub') return canManageSettings(roles, isPlatformAdmin);
  // Chantier : l'exploitant, personne d'autre. Aucun pack, aucun périmètre ne
  // l'ouvre — c'est ce qui garantit qu'un écran inachevé n'atteint pas un client.
  if (adminOnlyBand(pageKey)) return Boolean(isPlatformAdmin);
  if (ALWAYS_ALLOWED.has(pageKey)) return true;
  const mids = modulesForPage(pageKey);
  if (!mids.length) return true;
  if (!mids.some((m) => (visibleModules || []).includes(m))) return false;
  const need = neededModule(pageKey);
  if (need && !(visibleModules || []).includes(need)) return false;
  return pageAllowedByScope(pageKey, scopes || {});
}
